import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, Instagram } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import { siteConfig } from '../mock/data';
import { trackClick } from '../lib/tracking';
import { useToast } from '../hooks/use-toast';
import { useSettings } from '../context/SettingsContext';
import SectionRenderer from '../components/page-builder/SectionRenderer';
import { getPage } from '../lib/pageDefaults';

const OLD_WA_DIGITS = /6282112923387|6281211737949|82112923387|81211737949/;
const OLD_WA_DISPLAY = /\+?62\s*821[-\s]?1292[-\s]?3387|\+?62\s*812[-\s]?1173[-\s]?7949(\s*\(Solo\))?/i;

function sanitizePhoneDisplay(value) {
  if (!value) return '';
  const s = String(value);
  if (OLD_WA_DISPLAY.test(s) || OLD_WA_DIGITS.test(s.replace(/\D/g, ''))) return '';
  return s;
}

function sanitizeWaDigits(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (OLD_WA_DIGITS.test(digits)) return '';
  return digits || String(value);
}

export default function Contact() {
  const { settings } = useSettings();
  const page = getPage(settings?.page_builder, '/kontak') || {};
  const c = page.content || {};
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  const address = c.address || settings.address || 'PONDOK MUTIARA MEK 28 - 30, Banjarbendo, Kec. Sidoarjo, Kabupaten Sidoarjo.';
  const email = c.email || settings.email || siteConfig.email;
  const waPrimary = settings.whatsapp_number || siteConfig.whatsappNumber || '6281217532692';
  const waDisplay = `+${waPrimary}`;
  const phone1 = sanitizePhoneDisplay(c.phone1) || waDisplay;
  const phone1Wa = sanitizeWaDigits(c.phone1Wa) || waPrimary;
  const phone2 = sanitizePhoneDisplay(c.phone2) || waDisplay;
  const phone2Wa = sanitizeWaDigits(c.phone2Wa) || waPrimary;
  const instagram = c.instagram || settings.instagram || siteConfig.instagram;
  const waLink = `https://api.whatsapp.com/send?phone=${phone1Wa}`;
  const showPhone2 = phone2Wa !== phone1Wa || phone2 !== phone1;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    trackClick('contact_form_submit', { name: form.name });
    setTimeout(() => {
      setSending(false);
      toast({
        title: 'Pesan Terkirim!',
        description: c.formSuccess || 'Terima kasih. Tim BelBel akan segera menghubungi Anda.'
      });
      setForm({ name: '', email: '', phone: '', message: '' });
    }, 900);
  };

  return (
    <>
      <PageBanner
        title={page.title || "Hubungi Kami"}
        subtitle={page.subtitle || "Ada pertanyaan atau ingin konsultasi? Tim BelBel siap membantu Anda."}
        breadcrumb={['Kontak']}
      />

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-japo-green text-white rounded-2xl p-8">
                <h3 className="font-bold text-2xl mb-6">{c.infoTitle || 'Informasi Kontak'}</h3>
                <div className="space-y-5">
                  <div className="flex gap-4">
                    <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-japo-yellow" />
                    </div>
                    <div>
                      <div className="font-bold mb-1">{c.officeLabel || 'Kantor Pusat'}</div>
                      <div className="text-white/80 text-sm leading-relaxed whitespace-pre-line">
                        {address}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-japo-yellow" />
                    </div>
                    <div>
                      <div className="font-bold mb-1">{c.phoneLabel || 'Telepon / WhatsApp'}</div>
                      {phone1 && (
                        <a href={`https://wa.me/${phone1Wa}`} className="text-white/80 text-sm hover:text-japo-yellow block">{phone1}</a>
                      )}
                      {showPhone2 && phone2 && (
                        <a href={`https://wa.me/${phone2Wa}`} className="text-white/80 text-sm hover:text-japo-yellow block">{phone2}</a>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-japo-yellow" />
                    </div>
                    <div>
                      <div className="font-bold mb-1">{c.emailLabel || 'Email'}</div>
                      <a href={`mailto:${email}`} className="text-white/80 text-sm hover:text-japo-yellow">{email}</a>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-japo-yellow" />
                    </div>
                    <div>
                      <div className="font-bold mb-1">{c.hoursLabel || 'Jam Operasional'}</div>
                      <div className="text-white/80 text-sm whitespace-pre-line">{c.hours || 'Senin - Sabtu: 08.00 - 17.00 WIB'}</div>
                      {c.hoursNote !== '' && (
                        <div className="text-white/80 text-sm">{c.hoursNote || 'Minggu: Libur'}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mt-8 pt-6 border-t border-white/10">
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick('whatsapp_contact_page')}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-japo-yellow hover:bg-[#D19400] text-gray-900 font-bold px-4 py-3 rounded-lg transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {c.waButtonText || 'WhatsApp'}
                  </a>
                  <a
                    href={instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white p-3 rounded-lg transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3 bg-gray-50 rounded-2xl p-8 border border-gray-100">
              <h3 className="font-extrabold text-2xl text-gray-900 mb-2">{c.formTitle || 'Kirim Pesan'}</h3>
              <p className="text-gray-600 text-sm mb-6">{c.formDescription || 'Isi form di bawah dan tim BelBel akan menghubungi Anda dalam 1x24 jam.'}</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nama Lengkap *</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-japo-green focus:ring-2 focus:ring-japo-green/20 bg-white"
                      placeholder="Nama Anda"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-japo-green focus:ring-2 focus:ring-japo-green/20 bg-white"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nomor WhatsApp *</label>
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-japo-green focus:ring-2 focus:ring-japo-green/20 bg-white"
                    placeholder="08xxxxxxxxxx"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Pesan *</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-japo-green focus:ring-2 focus:ring-japo-green/20 bg-white resize-none"
                    placeholder="Tuliskan pesan Anda..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full inline-flex items-center justify-center gap-2 bg-japo-green hover:bg-japo-green-dark disabled:opacity-70 text-white font-bold px-6 py-3.5 rounded-xl shadow-md transition-all"
                >
                  <Send className="w-4 h-4" />
                  {sending ? 'Mengirim...' : 'Kirim Pesan'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <SectionRenderer sections={page.sections || []} />
    </>
  );
}
